/**
 * m3 echo Frontend - Shared React Query Client
 *
 * The client lives outside the component tree so that the logout path and the
 * identity guard in App can drop cached data without re-creating the provider.
 *
 * Security:
 * - Cached responses belong to the identity that fetched them. On every account
 *   or tenant switch the cache has to be dropped, otherwise data of the previous
 *   tenant can still be served from memory in the same browser tab.
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,          // 30s - data stays fresh for 30 seconds
      gcTime: 300000,             // 5min - keep unused data in cache for 5 minutes
      retry: 1,                   // Retry failed requests once
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
    },
  },
});

/**
 * Cancel in-flight requests and drop everything the previous identity cached.
 *
 * Called on logout and whenever the active user/tenant changes without a full
 * page reload.
 */
export const resetQueryCache = async (): Promise<void> => {
  try {
    await queryClient.cancelQueries();
  } finally {
    queryClient.clear();
  }
};
